import { inject, Injectable, NgZone } from '@angular/core';
import { EditRouteService } from 'src/app/pages/custom-route/shared/planner/components/route-timeline/components/daily-route-list/services';
import { ShowComponentService } from './show-component.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { END_TIME_HOUR } from 'src/app/pages/custom-route/shared/planner/components/route-timeline/config/constants';

@Injectable({ providedIn: 'root' })
export class ShowTimelineOptions {
  private _editRouteService = inject(EditRouteService);
  private _showComponentService = inject(ShowComponentService);
  private _breakpointsService = inject(BreakpointsService);
  private _ngZone = inject(NgZone);

  public triggerSlotClick(
    dayIndex: number,
    slotHour: number
  ): { is_successful: boolean; reason?: string } {
    const route = this._editRouteService.editedCustomRouteData();
    if (!route?.dailyRoutes) {
      return { is_successful: false, reason: 'No hay ruta generada' };
    }

    const dailyRoute = route.dailyRoutes[dayIndex];
    if (!dailyRoute) {
      return { is_successful: false, reason: `El día ${dayIndex + 1} no existe en la ruta` };
    }

    const slotId = this._resolveSlotId(dayIndex, slotHour, dailyRoute.points);
    if (!slotId) {
      return { is_successful: false, reason: `No hay slot disponible a las ${slotHour}:00 en el día ${dayIndex + 1}` };
    }

    if (this._breakpointsService.isMobile()) {
      this._showComponentService.showTimelineComponent();
      this._waitForSlotAndClick(slotId);
    } else {
      this._clickSlot(slotId);
    }

    return { is_successful: true };
  }

  private _waitForSlotAndClick(slotId: string, maxAttempts = 20): void {
    let attempts = 0;
    this._ngZone.runOutsideAngular(() => {
      const interval = setInterval(() => {
        attempts++;
        const el = document.getElementById(slotId);
        if (el) {
          clearInterval(interval);
          this._ngZone.run(() => this._clickSlot(slotId));
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.warn(`ShowTimelineOptions: slot ${slotId} not found after ${maxAttempts} attempts`);
        }
      }, 50);
    });
  }

  private _clickSlot(slotId: string): void {
    const slotElement = document.getElementById(slotId);
    if (!slotElement) {
      console.warn(`ShowTimelineOptions: slot ${slotId} not found in DOM`);
      return;
    }

    const clickTarget = slotElement.querySelector<HTMLElement>('.add-point-card');
    if (!clickTarget) {
      console.warn(`ShowTimelineOptions: .add-point-card not found inside ${slotId}`);
      return;
    }

    clickTarget.click();
  }

  private _resolveSlotId(dayIndex: number, targetHour: number, points: any[]): string | null {
    // Grid start is dynamic per route (HRS-2640).
    const startHour = this._editRouteService.routeStartHour();

    if (!points?.length) {
      // Empty-day slots render as slot-X-0-N, N relative to the grid start.
      if (targetHour < startHour || targetHour >= END_TIME_HOUR) return null;
      return `slot-${dayIndex}-0-${targetHour - startHour}`;
    }

    // Leading slots: before the first point
    const firstStartHour = new Date(points[0].startDate).getUTCHours();
    if (targetHour >= startHour && targetHour < firstStartHour) {
      const subIndex = targetHour - startHour;
      return `slot-${dayIndex}-0-${subIndex}`;
    }

    // Gap slots: between consecutive points
    for (let i = 1; i < points.length; i++) {
      const prevEndHour = new Date(points[i - 1].endDate).getUTCHours();
      const nextStartHour = new Date(points[i].startDate).getUTCHours();
      if (targetHour >= prevEndHour && targetHour < nextStartHour) {
        const subIndex = targetHour - prevEndHour;
        return `slot-${dayIndex}-${i}-${subIndex}`;
      }
    }

    // Trailing slots: after the last point
    const lastEndHour = new Date(points[points.length - 1].endDate).getUTCHours();
    if (targetHour >= lastEndHour && targetHour < END_TIME_HOUR) {
      const subIndex = targetHour - lastEndHour;
      return `slot-${dayIndex}-${points.length}-${subIndex}`;
    }

    return null;
  }
}
